import { useUserStore } from '#stores/user.store';
import type { AvailableLanguagesType, TranslationKey } from '@carrefour-gcs/shared';
import { createGlobalState } from '@vueuse/core';
import dayjs from 'dayjs';
import type { TOptions } from 'i18next';
import { useTranslation } from 'i18next-vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

/** Shared composable to manage the language of the app */
export const useLanguage = createGlobalState(() => {
  const { i18next, t } = useTranslation();
  const isLoading = ref(false);

  const userStore = useUserStore();
  const { authenticatedUser } = storeToRefs(userStore);

  const adapter: Record<AvailableLanguagesType, string> = {
    'fr-FR': 'fr-FR',
    'fr-BE': 'fr-BE',
    en: 'en-GB',
    es: 'es-ES'
  } as const;

  return {
    /**
     * Set the language of the app to the given language code and load translations
     *
     * @param language The language code to set
     * @example setLanguage('en')
     */
    setLanguage: async (language: AvailableLanguagesType) => {
      isLoading.value = true;
      try {
        // Update language settings
        dayjs.locale(language);
        await i18next.changeLanguage(language);
      } catch (error) {
        console.error(`Failed to load translations for ${language}:`, error);
        // Still change language even if translations fail
        dayjs.locale(language);
        await i18next.changeLanguage(language);
      } finally {
        isLoading.value = false;
      }
    },

    /**
     * A computed ref of the current language of the app.
     *
     * @example ComputedRef { value: 'en' }
     */
    language: computed(() => i18next.resolvedLanguage as AvailableLanguagesType),

    /**
     * List of available app's languages.
     *
     * @example Lang[] ['fr-FR', 'sp', 'en']
     */
    languages: Object.keys(i18next.services.resourceStore.data) as AvailableLanguagesType[],

    /**
     * Translate a string with the given key and options.
     *
     * @param key The key of the string to translate
     * @param options The options to pass to the translation
     * @example t('myKey', { count: 2 })
     */
    t: t as (key: TranslationKey | (string & {}), options?: TOptions) => string,

    /**
     * A computed ref of the default language of the user's settings.
     *
     * @example ComputedRef { value: 'en' }
     */
    userDefaultLanguage: computed(() => authenticatedUser.value?.appScope?.lang),

    /**
     * A computed ref of local identifier based on current language.
     *
     * @example ComputedRef { value: 'en-GB' }
     */
    localLanguage: computed(() => {
      const language = i18next.resolvedLanguage as AvailableLanguagesType | undefined;
      return adapter[language ?? 'en'] ?? adapter['en'];
    }),

    /**
     * Loading state for translation updates
     */
    isLoading: computed(() => isLoading.value)
  };
});
